import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { toast } from '@/lib/v2/components';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { formValidations } from '@/src/constants/FormValidations';

import { atomRuleConfigCreation } from '@/modules/CampaignsModule/atoms/createActionsAtom';
import {
  IValidationResource,
  useValidNameResource,
} from '@/modules/CampaignsModule/hooks/useValidNameResource';
import { EResourceType } from '@/modules/CampaignsModule/interfaces/CampaignActions';
import { atomToggleCreateRuleModal, atomToggleSidePanel } from '@/modules/RulesModule/atoms/rules';
import useRuleCreateRule from '@/modules/RulesModule/hooks/useCreateRule';
import { RuleTypes } from '@/modules/RulesModule/interfaces';

export const useCreateRuleModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { campaignId } = useParams();

  const showRulesModuleV2 = useFeatureFlag('showRulesModuleV2');

  const { validateNameResource, loadingValidation } = useValidNameResource();
  const {
    events: { createRule },
    state: { loading },
  } = useRuleCreateRule();

  const setToggleCreateRuleModal = useSetAtom(atomToggleCreateRuleModal);
  const ruleConfigCreation = useAtomValue(atomRuleConfigCreation);
  const setToggleSidePanel = useSetAtom(atomToggleSidePanel);

  const { formRegExp, inputLengths } = formValidations;

  const RULE_VALIDATION_RULES = useMemo(
    () => ({
      input: {
        required: {
          value: true,
          message: t('FORMS_RULES_MESSAGES.required'),
        },
        minLength: {
          value: inputLengths.min,
          message: t('FORMS_RULES_MESSAGES.minLength'),
        },
        maxLength: {
          value: inputLengths.maxAction,
          message: t('FORMS_RULES_MESSAGES.maxLengthAction'),
        },
        pattern: {
          value: formRegExp.lettersNumbersAndSpecials,
          message: t('FORMS_RULES_MESSAGES.lettersNumbersAndSpecials'),
        },
      },
    }),
    [formRegExp.lettersNumbersAndSpecials, inputLengths.max, inputLengths.min, t]
  );

  const handleCloseRuleModal = useCallback(() => {
    setToggleCreateRuleModal(false);
  }, [setToggleCreateRuleModal]);

  const handleCreateRule = useCallback(
    async (name: string) => {
      if (!campaignId || !ruleConfigCreation) return;

      const currentRuleId = await createRule({
        campaignId,
        actionName: name,
        actionType: ruleConfigCreation.actionType,
        ruleType: ruleConfigCreation.ruleType,
      });

      if (!currentRuleId) {
        toast({
          id: 'ruleError',
          title: t(`useCreateRule.email_rules.error.title`),
          body: t(`useCreateRule.email_rules.error.body`),
          variant: 'error',
        });

        return;
      }

      const ruleBasePath = `${ruleConfigCreation.actionType}/${currentRuleId}`;

      toast({
        id: 'ruleCreated',
        title: t(`useCreateRule.email_rules.success.title`),
        body: t(`useCreateRule.email_rules.success.body`),
      });

      handleCloseRuleModal();

      ruleConfigCreation.ruleType === RuleTypes.FLOW_TEMPLATE && showRulesModuleV2
        ? navigate(`${ruleBasePath}/email`)
        : navigate(ruleBasePath);

      setToggleSidePanel(true);
    },
    [
      campaignId,
      ruleConfigCreation,
      showRulesModuleV2,
      createRule,
      handleCloseRuleModal,
      navigate,
      setToggleSidePanel,
      t,
    ]
  );

  const validateRuleName = useCallback(
    async (value: string, message: string): Promise<IValidationResource> => {
      const isValidResource = await validateNameResource({
        name: value,
        resource: EResourceType.ACTION,
        campaignId: Number(campaignId),
      });

      return { isValidInput: isValidResource, message };
    },
    [validateNameResource, campaignId]
  );

  return {
    events: {
      handleCloseRuleModal,
      handleCreateRule,
      validateRuleName,
    },
    state: {
      processing: loading,
      loadingValidation,
      RULE_VALIDATION_RULES,
    },
  };
};
